import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TablePagination,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { display } from "html2canvas/dist/types/css/property-descriptors/display";

interface Props {
  headers: string[];
  data: any[];
  loading?: boolean;
  page?: number;
  rowsPerPage?: number;
  height?: string | number;
  tableMinWidth?: number | string;
  columnMinWidths?: Record<string, number | string>;
  nowrapHeaders?: boolean;
  handleChangePage?: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  handleChangeRowsPerPage?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  totalRows?: number;
  actionButtons?: {
    icon?: React.ReactNode | ((rowIndex: number) => React.ReactNode);
    onClick?: (rowIndex: number, event?: React.MouseEvent<HTMLElement>) => void;
    disabled?: (rowIndex: number) => boolean;
  }[];
  rowStyleCondition?: (rowIndex: number) => {
    sx: React.CSSProperties;
    className: string;
  };
}

const DynamicTable: React.FC<Props> = ({
  headers,
  data,
  loading = false,
  page = 0,
  rowsPerPage = 100,
  handleChangePage,
  handleChangeRowsPerPage,
  totalRows = 0,
  actionButtons,
  rowStyleCondition,
  height = "75vh",
  tableMinWidth,
  columnMinWidths,
  nowrapHeaders = false,
}) => {
  const getColumnMinWidth = (header: string, index: number) =>
    columnMinWidths?.[header] ?? columnMinWidths?.[String(index)];

  return (
    <div style={{ overflowX: "auto", width: "100%" }}>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: { md: `${height}`, sm: "100vh", xs: "100vh" },
          width: "100%",
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#077DD3",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(7, 125, 211, 0.24)",
          },
        }}
      >
        <Table
          sx={{
            minWidth: tableMinWidth || "100%",
            tableLayout: tableMinWidth ? "fixed" : "auto",
          }}
        >
          <TableHead sx={{ position: "sticky", top: 0, zIndex: 1 }}>
            <TableRow sx={{ backgroundColor: "#077DD3", color: "#fff" }}>
              {headers?.map((header, index) => (
                <TableCell
                  sx={{
                    fontSize: {
                      md: "0.8em",
                      xs: "0.6em",
                      xl: "0.8em",
                      sm: "0.6em",
                      lg: "0.8em",
                    },
                    p: 0.5,
                    px: 1,
                    color: "white",
                    whiteSpace: nowrapHeaders ? "nowrap" : "normal",
                    minWidth: getColumnMinWidth(header, index),
                    width: getColumnMinWidth(header, index),
                    verticalAlign: "middle",
                  }}
                  key={index}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {loading ? (
            <TableRow>
              <TableCell
                colSpan={headers.length + (actionButtons ? 1 : 0)}
                sx={{ textAlign: "center" }}
              >
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : (
            <TableBody>
              {data?.map((row, rowIndex) => {
                const { sx, className } = rowStyleCondition
                  ? rowStyleCondition(rowIndex)
                  : { sx: {}, className: "" };
                const rowSx = { ...(sx || {}) } as Record<string, any>;
                const cellFromRow = rowSx["& > .MuiTableCell-root"] || {};
                delete rowSx["& > .MuiTableCell-root"];
                return (
                  <TableRow
                    key={rowIndex}
                    className={className}
                    sx={{
                      "& > .MuiTableCell-root": {
                        ...(tableMinWidth
                          ? { py: 0.75, verticalAlign: "top" }
                          : { height: "20px", py: 0.5 }),
                        ...cellFromRow,
                      },
                      ...rowSx,
                    }}
                  >
                    {Object.values(row ?? {})?.map((cell: any, cellIndex) => {
                      const header = headers?.[cellIndex];
                      const minWidth = getColumnMinWidth(header, cellIndex);
                      return (
                      <TableCell
                        sx={{
                          fontSize: {
                            md: "0.8em",
                            xs: "0.6em",
                            xl: "0.8em",
                            sm: "0.6em",
                            lg: "0.8em",
                          },
                          px: 1,
                          py: 0.75,
                          textTransform: "capitalize",
                          minWidth,
                          width: minWidth,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          verticalAlign: "top",
                        }}
                        key={cellIndex}
                      >
                        {cell}
                      </TableCell>
                      );
                    })}
                    {actionButtons && actionButtons?.length > 0 && (
                      <TableCell
                        style={{
                          display: "flex",
                          justifyContent: "start",
                          alignItems: "center",
                          height: "100%",
                        }}
                        sx={{
                          fontSize: {
                            md: "0.8em",
                            xs: "0.6em",
                            xl: "0.8em",
                            sm: "0.6em",
                            lg: "0.8em",
                          },
                          p: "2px",
                        }}
                        align="left"
                      >
                        {actionButtons?.map((button, buttonIndex) => (
                          <React.Fragment key={buttonIndex}>
                            {button.icon && (
                              <IconButton
                                onClick={(event) =>
                                  button.onClick?.(rowIndex, event)
                                }
                                sx={{
                                  "&:hover": { background: "none" },
                                }}
                                disabled={button.disabled?.(rowIndex)}
                              >
                                {typeof button.icon === "function"
                                  ? button.icon(rowIndex)
                                  : button.icon}
                              </IconButton>
                            )}
                          </React.Fragment>
                        ))}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
        {totalRows > 0 && (
          <TablePagination
            sx={{
              fontSize: {
                md: "0.6em",
                xs: "0.6em",
                xl: "0.8em",
                sm: "0.6em",
                lg: "0.8em",
              },
              position: "sticky",
              bottom: 0,
              zIndex: 1,
              background: "white",
              height: "100%",
              padding: "0px",
              width: "100%",
              backgroundColor: "#077DD3",
              color: "#fff",
              ".css-78c6dr-MuiToolbar-root-MuiTablePagination-toolbar": {
                minHeight: "20px",
              },
            }}
            rowsPerPageOptions={[100, 150, 200]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage || 100}
            page={page}
            onPageChange={handleChangePage || (() => {})}
            onRowsPerPageChange={handleChangeRowsPerPage || (() => {})}
          />
        )}
      </TableContainer>
    </div>
  );
};

export default DynamicTable;
